/* eslint-disable max-len */
import { GARAGE_UPDATE, GET_GARAGE_INFO_RESPONSE } from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { Garage } from '@1po/1po-bff-fe-spec/generated/garage/Garage';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { getFirstValidAddress } from 'domains/garage/Garage.utils';
import { DealerRepository, getTradingProfile } from 'domains/user';
import { getData, NO_DATA } from 'utils';
import {
  CHANGE_GARAGE_EMAIL,
  CHANGE_WORKSHOP_ID,
  GARAGE_NAMESPACE,
  GarageState,
  GET_GARAGE_INFO,
  VERIFY_DMS_WORKSHOP_ID_REQUEST,
} from './Garage.types';

const initialState: GarageState = {
  garages: new Map<string, Garage | NO_DATA>(),
};

export const fetchGarageInfoRequest = createAction<{ garageId: string; repository: DealerRepository }>(GET_GARAGE_INFO);
export const fetchGarageInfoResponse = createAction(GET_GARAGE_INFO_RESPONSE);
export const fetchChangeGarageEmailRequest = createAction<string>(CHANGE_GARAGE_EMAIL);
export const fetchGarageUpdate = createAction<string>(GARAGE_UPDATE);
export const configureWorkshopId = createAction<string>(CHANGE_WORKSHOP_ID);
export const verifyDmsWorkshopIdRequestSaga = createAction<{
  workshopId: string;
}>(VERIFY_DMS_WORKSHOP_ID_REQUEST);

// Slice
const slice = createSlice({
  name: GARAGE_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setGarage: (state, action: PayloadAction<{ garageId: string; garage: Garage | NO_DATA }>) => {
      const { payload } = action;
      state.garages.set(payload.garageId, payload.garage);
    },
    setGarageNoDataStatus: (state, action: PayloadAction<{ garageId: string; status: NO_DATA }>) => {
      const { garageId, status } = action.payload;
      state.garages.set(garageId, status);
    },
    setGarageEmail: (state, action: PayloadAction<{ garageId: string; email: string }>) => {
      const { garageId, email } = action.payload;
      const garage = getData(state.garages.get(garageId));
      if (garage === undefined) return;
      const address = getFirstValidAddress(garage);
      if (address) {
        address.email = email;
      }
    },
    setGarageWorkshopId: (state, action: PayloadAction<{ garageId: string; workshopId: string }>) => {
      const { garageId, workshopId } = action.payload;
      const garage = getData(state.garages.get(garageId));
      if (garage === undefined) return;
      garage.workshopId = workshopId;
    },
  },
});

//Actions
export const { setInitialState, setGarage, setGarageNoDataStatus, setGarageEmail, setGarageWorkshopId } = slice.actions;

// Getters/Selectors
export const getUserGarageInfo = createSelector(
  (state: RootState) => state.garage.garages,
  getTradingProfile,
  (garages, tradingProfile): Garage | NO_DATA => {
    return garages.get(tradingProfile?.buyerId ?? '');
  },
);

export const getGarageInfo = createSelector(
  (state: RootState) => state.garage.garages,
  (_state: RootState, garageId: string) => garageId,
  (garages, garageId) => garages.get(garageId),
);

export const getUserGarageWorkshopId = createSelector(getUserGarageInfo, (garage) => getData(garage)?.workshopId);

// Export reducer
export default slice.reducer;
